import React, { useState, useEffect, useCallback } from 'react';
import { BellRing, RefreshCw, Play, Send, Check, Mail } from 'lucide-react';
import { apiService } from '../../services/apiService';

// ---- Typen (Spiegel der reminderCron-Strukturen) ----
interface Tier {
  key: string;
  label: string;
  hoursBefore?: number;
  enabled: boolean;
}
interface ReminderSettings {
  enabled: boolean;
  tiers: Tier[];
}
interface UpcomingTier {
  key: string;
  label: string;
  enabled: boolean;
  due: boolean;
  status: 'pending' | 'sent' | 'failed' | 'skipped';
  sent_at: string | null;
  trigger: string | null;
  error: string | null;
}
interface UpcomingAppt {
  id: number;
  patient_name: string;
  patient_email: string | null;
  language: string;
  appointment_date: string;
  start_time: string;
  hours_until: number;
  tiers: UpcomingTier[];
}
interface LogEntry {
  id: number;
  appointment_id: number;
  tier: string;
  recipient: string | null;
  language: string | null;
  status: 'sent' | 'failed' | 'skipped';
  message_id: string | null;
  error: string | null;
  skip_reason: string | null;
  patient_name: string | null;
  appointment_date: string | null;
  start_time: string | null;
  trigger: string;
  sent_by: string;
  sent_at: string;
}

const LANG_LABEL: Record<string, string> = { de: '🇩🇪 DE', en: '🇬🇧 EN', es: '🇪🇸 ES', ru: '🇷🇺 RU' };

function langLabel(code?: string | null) {
  if (!code) return '—';
  return LANG_LABEL[code] || code.toUpperCase();
}

function fmtDateTime(ts: string) {
  return new Date(ts).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StatusPill({ status }: { status: string }) {
  const MAP: Record<string, string> = {
    sent: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    skipped: 'bg-gray-100 text-gray-600',
    pending: 'bg-amber-100 text-amber-700',
  };
  const LABEL: Record<string, string> = {
    sent: 'Gesendet', failed: 'Fehler', skipped: 'Übersprungen', pending: 'Ausstehend',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${MAP[status] || 'bg-gray-100 text-gray-600'}`}>
      {LABEL[status] || status}
    </span>
  );
}

const LOG_STATUS_FILTERS = [
  { key: 'all', label: 'Alle' },
  { key: 'sent', label: 'Gesendet' },
  { key: 'failed', label: 'Fehler' },
  { key: 'skipped', label: 'Übersprungen' },
];

export const RemindersPage: React.FC = () => {
  const [settings, setSettings] = useState<ReminderSettings | null>(null);
  const [upcoming, setUpcoming] = useState<UpcomingAppt[]>([]);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [running, setRunning] = useState(false);
  const [sendingKey, setSendingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sRes, uRes, lRes] = await Promise.all([
        apiService.getReminderSettings(),
        apiService.getUpcomingReminders(),
        apiService.getReminderLog(),
      ]);
      setSettings((sRes.data as any).data);
      setUpcoming((uRes.data as any).data ?? []);
      setLog((lRes.data as any).data ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flashNotice = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 4000);
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    setError(null);
    try {
      const payload = {
        enabled: settings.enabled,
        tiers: settings.tiers.map(t => ({ key: t.key, enabled: t.enabled })),
      };
      const res = await apiService.updateReminderSettings(payload);
      setSettings((res.data as any).data);
      flashNotice('Einstellungen gespeichert.');
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Speichern fehlgeschlagen');
    } finally {
      setSavingSettings(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await apiService.runReminders(false);
      const s = (res.data as any).data;
      flashNotice(`Lauf abgeschlossen: ${s.sent} gesendet, ${s.skipped} übersprungen, ${s.failed} Fehler.`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Lauf fehlgeschlagen');
    } finally {
      setRunning(false);
    }
  };

  const sendOne = async (appt: UpcomingAppt, tierKey: string) => {
    const key = `${appt.id}:${tierKey}`;
    setSendingKey(key);
    setError(null);
    try {
      const res = await apiService.sendReminder(appt.id, tierKey);
      if ((res.data as any).success) {
        flashNotice(`Erinnerung an ${appt.patient_name} gesendet.`);
      } else {
        setError((res.data as any).message || 'Versand nicht möglich');
      }
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Versand fehlgeschlagen');
    } finally {
      setSendingKey(null);
    }
  };

  const setTierEnabled = (key: string, enabled: boolean) => {
    setSettings(prev => prev ? { ...prev, tiers: prev.tiers.map(t => t.key === key ? { ...t, enabled } : t) } : prev);
  };

  const filteredLog = log.filter(e => statusFilter === 'all' || e.status === statusFilter);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <BellRing className="h-5 w-5 text-brand-primary" strokeWidth={1.75} />
          <h1 className="text-lg font-semibold text-brand-text">Erinnerungen</h1>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-accent/10 text-brand-primary uppercase tracking-wider">
            Automatisch
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runNow}
            disabled={running || loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-md bg-brand-primary text-white hover:opacity-90 transition disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
          >
            <Play className={`h-3.5 w-3.5 ${running ? 'animate-pulse' : ''}`} strokeWidth={2} />
            Jetzt prüfen &amp; senden
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-md bg-brand-surface border border-brand-border text-brand-text hover:bg-brand-surface-2 transition disabled:opacity-50 cursor-pointer"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={2} />
            Neu laden
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">{error}</div>
      )}
      {notice && (
        <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-[13px] text-green-700">{notice}</div>
      )}

      {/* Einstellungen */}
      {settings && (
        <div className="bg-brand-surface border border-brand-border rounded-lg p-5">
          <h2 className="text-[13px] font-semibold text-brand-text mb-1">Einstellungen</h2>
          <p className="text-[12px] text-brand-text-muted mb-4">
            Automatische Erinnerungen werden nur an Termine mit Status <strong>bestätigt</strong> verschickt,
            in der Sprache des Termins (Muttersprache + Englisch). Der Versand läuft stündlich (Zeitzone Europe/Madrid).
          </p>

          <label className="flex items-center gap-3 mb-4 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={e => setSettings({ ...settings, enabled: e.target.checked })}
              className="h-4 w-4 rounded border-brand-border text-brand-primary focus:ring-brand-primary/30"
            />
            <span className="text-[13px] text-brand-text font-medium">Automatische Erinnerungen aktiv</span>
          </label>

          <div className="space-y-2 mb-4">
            <p className="text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold">Vorlaufzeiten</p>
            {settings.tiers.map(t => (
              <label key={t.key} className={`flex items-center gap-3 cursor-pointer select-none ${settings.enabled ? '' : 'opacity-50'}`}>
                <input
                  type="checkbox"
                  checked={t.enabled}
                  disabled={!settings.enabled}
                  onChange={e => setTierEnabled(t.key, e.target.checked)}
                  className="h-4 w-4 rounded border-brand-border text-brand-primary focus:ring-brand-primary/30"
                />
                <span className="text-[13px] text-brand-text">
                  {t.label} <span className="text-brand-text-muted">vor dem Termin</span>
                </span>
              </label>
            ))}
          </div>

          <button
            onClick={saveSettings}
            disabled={savingSettings}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-[12px] font-medium rounded-md bg-brand-primary text-white hover:opacity-90 transition disabled:opacity-50 cursor-pointer"
          >
            <Check className="h-3.5 w-3.5" strokeWidth={2} />
            {savingSettings ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      )}

      {/* Bevorstehende Termine */}
      <div className="bg-brand-surface border border-brand-border rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-brand-border flex items-center gap-2">
          <Mail className="h-4 w-4 text-brand-primary" strokeWidth={1.75} />
          <h2 className="text-[13px] font-semibold text-brand-text">Bevorstehende bestätigte Termine</h2>
          <span className="text-[11px] text-brand-text-muted ml-auto">{upcoming.length} Termine (nächste 8 Tage)</span>
        </div>
        {loading && upcoming.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-brand-text-muted">Lädt…</div>
        ) : upcoming.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-brand-text-muted">Keine bevorstehenden bestätigten Termine.</div>
        ) : (
          <div className="divide-y divide-brand-border">
            <div className="hidden md:grid grid-cols-[1fr_140px_70px_70px_1fr] gap-3 px-5 py-2 text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold bg-brand-surface-2">
              <span>Patient</span>
              <span>Termin</span>
              <span>Sprache</span>
              <span className="text-right">In</span>
              <span>Erinnerungen</span>
            </div>
            {upcoming.map(appt => (
              <div key={appt.id} className="grid md:grid-cols-[1fr_140px_70px_70px_1fr] gap-3 px-5 py-3 items-center text-[12px]">
                <div className="min-w-0">
                  <div className="font-medium text-brand-text truncate">{appt.patient_name}</div>
                  <div className="text-brand-text-muted text-[11px] truncate">{appt.patient_email || 'keine E-Mail'}</div>
                </div>
                <div className="text-brand-text">
                  {new Date(appt.appointment_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} · {appt.start_time}
                </div>
                <div className="text-brand-text">{langLabel(appt.language)}</div>
                <div className="text-right text-brand-text-muted">{appt.hours_until}h</div>
                <div className="flex flex-wrap gap-2">
                  {appt.tiers.map(t => {
                    const key = `${appt.id}:${t.key}`;
                    const sending = sendingKey === key;
                    return (
                      <div key={t.key} className="inline-flex items-center gap-1.5 bg-brand-surface-2 rounded-md px-2 py-1">
                        <span className="text-[11px] text-brand-text-muted">{t.label}:</span>
                        {t.status === 'sent' ? (
                          <span className="text-[11px] text-green-700" title={t.sent_at ? fmtDateTime(t.sent_at) : ''}>✓ gesendet</span>
                        ) : t.status === 'skipped' ? (
                          <span className="text-[11px] text-gray-500">übersprungen</span>
                        ) : (
                          <span className="text-[11px] text-amber-600">{t.due ? 'fällig' : 'geplant'}</span>
                        )}
                        <button
                          onClick={() => sendOne(appt, t.key)}
                          disabled={sending || !appt.patient_email}
                          title={!appt.patient_email ? 'Keine E-Mail-Adresse' : 'Jetzt senden'}
                          className="inline-flex items-center gap-1 text-[11px] text-brand-primary hover:underline disabled:opacity-40 disabled:no-underline cursor-pointer disabled:cursor-not-allowed"
                        >
                          <Send className={`h-3 w-3 ${sending ? 'animate-pulse' : ''}`} strokeWidth={2} />
                          {sending ? '…' : 'senden'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Protokoll */}
      <div className="bg-brand-surface border border-brand-border rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-brand-border flex items-center gap-3 flex-wrap">
          <BellRing className="h-4 w-4 text-brand-primary" strokeWidth={1.75} />
          <h2 className="text-[13px] font-semibold text-brand-text">Protokoll</h2>
          <div className="flex items-center gap-1 bg-brand-surface-2 border border-brand-border rounded-md p-0.5 ml-auto">
            {LOG_STATUS_FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded transition cursor-pointer ${
                  statusFilter === f.key ? 'bg-brand-primary text-white shadow-sm' : 'text-brand-text-muted hover:text-brand-text'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        {filteredLog.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-brand-text-muted">Noch keine Erinnerungen protokolliert.</div>
        ) : (
          <div className="divide-y divide-brand-border">
            <div className="hidden md:grid grid-cols-[150px_1fr_80px_70px_90px_1fr] gap-3 px-5 py-2 text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold bg-brand-surface-2">
              <span>Zeitpunkt</span>
              <span>Empfänger</span>
              <span>Vorlauf</span>
              <span>Sprache</span>
              <span>Auslöser</span>
              <span>Status / Details</span>
            </div>
            {filteredLog.map(e => (
              <div key={e.id} className="grid md:grid-cols-[150px_1fr_80px_70px_90px_1fr] gap-3 px-5 py-3 items-center text-[12px]">
                <div className="text-brand-text font-mono text-[11px]">{fmtDateTime(e.sent_at)}</div>
                <div className="min-w-0">
                  <div className="text-brand-text truncate">{e.patient_name || '—'}</div>
                  <div className="text-brand-text-muted text-[11px] truncate">{e.recipient || '—'}</div>
                </div>
                <div className="text-brand-text">{e.tier === '24h' ? '24 Std.' : e.tier === '7d' ? '1 Woche' : e.tier}</div>
                <div className="text-brand-text">{langLabel(e.language)}</div>
                <div className="text-brand-text-muted text-[11px]">
                  {e.trigger === 'manual' ? (
                    <span title={e.sent_by}>Manuell<br /><span className="text-[10px]">{e.sent_by}</span></span>
                  ) : 'Automatisch'}
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <StatusPill status={e.status} />
                  {e.error && <span className="text-[11px] text-red-600 truncate" title={e.error}>{e.error}</span>}
                  {e.skip_reason && <span className="text-[11px] text-brand-text-muted truncate" title={e.skip_reason}>{e.skip_reason}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RemindersPage;
